import type { PlanningBaseline } from '@@/api/statistics/planning/planning-baseline';
import type { ProjectionsResponse } from '@@/api/statistics/timeline/projections-handler';
import type { ExperimentPipeline } from 'types';
import type { LibraryRow } from './library-learnings';
import { useEffect, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useQueryState } from 'hooks/useQueryState';
import { resolveWeights } from 'helpers/weight-distribution';
import { resolveObjectives } from 'helpers/objectives';
import { config } from 'config';
import { useWebsites } from '@/state/useWebsites';
import { usePipelines } from '@/state/pipelines/usePipelines';
import { useAttention } from '@/state/useAttention';
import { ALL_TIME, TIMELINE_RANGES, buildTimeline, inWindow, needsPlannedLength, timelineWindow } from './timeline';
import { parsePlanAssumptions } from 'helpers/plan-assumptions';
import { evaluatePlan } from 'helpers/experiment-plan';

export { ALL_TIME, TIMELINE_RANGES };

/** How often "now" moves. Once a minute is finer than any bar, and steady between renders. */
const CLOCK_TICK_MS = 60_000;

type UseExperimentTimelineParams = {
  /** Every experiment on the website: pipeline dates depend on all of a stage's members. */
  allRows: readonly LibraryRow[];
  /** What matches the filter and the search: the rows that are drawn. */
  visibleRows: readonly LibraryRow[];
};

/**
 * The Library's timeline layout: every experiment on a time axis.
 *
 * Built from every experiment and then narrowed to the visible rows, so the filter and
 * the search choose *which* bars are drawn but never move them, or the scale they are
 * drawn at. Building from the narrowed rows once left queued drafts undatable, because
 * the running stage ahead of them had been filtered out.
 */
export const useExperimentTimeline = ({ allRows, visibleRows }: UseExperimentTimelineParams) => {
  const rows = allRows;
  const statisticsClient = useClient(config.STATISTICS_URL);
  const { website } = useWebsites();
  const websiteId = website?.id;
  const { pipelines, isLoading: isLoadingPipelines } = usePipelines();
  const { items: attention } = useAttention(websiteId);
  // All time by default: the timeline is where a programme's history is read, and a
  // fixed window hid everything older than it on first open.
  const [range, setRange] = useQueryState<number>('range', ALL_TIME);

  // Archived rows are drawn, so they are estimated too: archiving is allowed in any
  // status, and the engine still runs an archived draft that is queued in a pipeline.
  const hasRunning = rows.some((row) => row.status === 'Running');
  const { data: projections, isLoading: isLoadingProjections } = useQuery<ProjectionsResponse>({
    enabled: !!websiteId && hasRunning,
    queryKey: ['TIMELINE_PROJECTIONS', websiteId],
    queryFn: () => statisticsClient.get<ProjectionsResponse>(`/projections/${websiteId}`),
    staleTime: 1000 * 60 * 5,
  });

  const pipelineList: ExperimentPipeline[] = pipelines ?? [];

  // Read once a minute rather than on every render: a fresh Date.now() each render gave
  // every bar a new position object, so a column-resize drag rebuilt the whole timeline
  // on every pixel.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // Drafts in the pending stages of a *running* pipeline: a Draft or Paused pipeline is
  // not advancing, so buildTimeline never dates what is queued in it, and fetching a plan
  // for those was a request whose answer went unused.
  const queued = new Set(
    pipelineList
      .filter((pipeline) => pipeline.status === 'Running')
      .flatMap((pipeline) => pipeline.stages.filter((stage) => stage.state === 'pending'))
      .flatMap((stage) => stage.items.map((item) => item.experimentId)),
  );
  // Only drafts someone planned in the wizard. A length is drawn from a plan a person
  // chose; nothing is estimated on their behalf, so a draft with no saved plan is listed
  // as unplanned instead of drawn at a default and fetched for.
  const planned = rows.flatMap((row) => {
    const assumptions = parsePlanAssumptions(row.plan);
    if (!assumptions || !needsPlannedLength({ experiment: row, queuedIds: queued, nowMs })) return [];
    return [{ row, assumptions }];
  });

  // The site's figures only where the plan uses them: a plan built on the planner's own
  // traffic and rate figures is computed without a request.
  const needsFigures = planned.filter(
    ({ assumptions }) => assumptions.dailySessions === null || assumptions.ratePct === null,
  );

  const baselines = useQueries({
    queries: needsFigures.map(({ row }) => ({
      // The planner's own key, so a draft planned on its Brief tab is not fetched twice.
      queryKey: ['PLANNING_BASELINE', row.id, resolveObjectives(row).primary],
      queryFn: () => statisticsClient.get<PlanningBaseline>(`/${row.id}/planning-baseline`),
      staleTime: 1000 * 60 * 10,
    })),
  });

  // Each planned draft is drawn as long as its own plan says, through the same
  // evaluatePlan as its Brief tab, so the timeline and the card cannot disagree.
  const figuresById = new Map(needsFigures.map(({ row }, index) => [row.id, baselines[index]?.data ?? null]));
  const plannedDays = new Map<number, number>();
  for (const { row, assumptions } of planned) {
    const baseline = figuresById.get(row.id) ?? null;
    if (figuresById.has(row.id) && !baseline) continue;
    const arms = (row.variants ?? []).filter((variant) => !variant.deployed);
    const weights = resolveWeights(arms)?.map((entry) => entry.weight) ?? arms.map(() => 1);
    const { plan } = evaluatePlan({ assumptions, baseline, weights });
    if (plan?.recommendedDays) plannedDays.set(row.id, plan.recommendedDays);
  }

  const timeline = buildTimeline({
    experiments: rows,
    pipelines: pipelineList,
    projections: projections ?? {},
    plannedDays,
    nowMs,
    visibleIds: new Set(visibleRows.map((row) => row.id)),
  });

  const months = (TIMELINE_RANGES as readonly number[]).includes(range) ? range : ALL_TIME;
  // From every bar, not the narrowed ones: typing a search must not rescale the axis.
  const { from, to } = timelineWindow({ bars: timeline.allBars, months, nowMs });

  const groups = timeline.groups
    .map((group) => ({ ...group, bars: group.bars.filter((bar) => inWindow(bar, from, to)) }))
    .filter((group) => group.bars.length > 0);

  // One finding per experiment, from the same detector as the Home attention card.
  const interactions = new Map(
    attention
      .filter((item) => item.kind === 'interaction')
      .map((item) => [item.experimentId, String(item.data.otherExperimentName ?? 'another experiment')]),
  );

  return {
    groups,
    unplaced: timeline.unplaced,
    hiddenByWindow:
      timeline.groups.reduce((n, g) => n + g.bars.length, 0) - groups.reduce((n, g) => n + g.bars.length, 0),
    from,
    to,
    nowMs,
    range: months,
    setRange,
    interactions,
    // Until the estimates arrive every queued draft would read as unplaceable and every
    // running bar as open-ended, so the timeline waits for them rather than showing
    // reasons that change a second later.
    isLoading: isLoadingPipelines || (hasRunning && isLoadingProjections) || baselines.some((query) => query.isLoading),
  };
};
